import { ThemeProvider } from 'styled-components';
import { Routes, Route } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { CookiesProvider } from 'react-cookie';

import './App.css';
import { theme } from './styles/styles';
import InputPage from './pages/InputPage/InputPage';
import LandingPage from './pages/LandingPage/LandingPage';
import Loading from './components/Loading/Loading';
import Analysis from './pages/AnalysisPage/AnalysisPage';
import Costs from './components/Costs/Costs';
import ROI from './components/ROI/ROI';
import Incentives from './components/Incentives/Incentives';
import FAQs from './components/FAQs/FAQs';
import Header from './components/Header/Header';
import AccessGate from './components/AccessGate/AccessGate';

import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { assignToken } from './features/token/token';
import { setShowAccessGate, setInvalidAccessKey, setGateChecked } from './features/accessGate/accessGate';
import Footer from './components/Footer/Footer';
import PDF from './components/PDF/pdf';

const GATE_ENABLED = process.env.REACT_APP_ACCESS_GATE_ENABLED === 'true';

function App() {
  const token = useSelector((state) => state.token.value);
  const showAccessGate = useSelector((state) => state.accessGate.showAccessGate);
  const invalidAccessKey = useSelector((state) => state.accessGate.invalidAccessKey);
  const gateChecked = useSelector((state) => state.accessGate.gateChecked);
  const dispatch = useDispatch();
  const [gateLoading, setGateLoading] = useState(false);

  // Gate is active if enabled and the user hasn't passed it yet
  const gateActive = GATE_ENABLED && (!gateChecked || showAccessGate);

  const buildAuthHeader = () => {
    const clientname = process.env.REACT_APP_APP_CLIENT;
    const instance = process.env.REACT_APP_APP_INSTANCE;
    return btoa(`Basic ${clientname}:${instance}`);
  };

  useEffect(() => {
    // Sync gate state on first load
    if (GATE_ENABLED && !gateChecked) {
      dispatch(setGateChecked(true));
      dispatch(setShowAccessGate(true));
    }

    // Fetch JWT when gate is not blocking and no token yet
    if (!gateActive && !token) {
      axios
        .get(`${process.env.REACT_APP_API_URL}api/login`, {
          headers: {
            Authorization: buildAuthHeader(),
            'Content-Type': 'application/json',
          },
        })
        .then((response) => {
          dispatch(assignToken(response.data['token']));
          console.log('Fetched token successfully');
        })
        .catch((error) => {
          console.error(error);
        });
    }
  });

  const handleAccessCodeSubmit = (code) => {
    setGateLoading(true);
    dispatch(setInvalidAccessKey(false));
    axios
      .post(`${process.env.REACT_APP_API_URL}api/validatekey`, { key: code })
      .then((response) => {
        if (response.data.valid) {
          dispatch(setShowAccessGate(false));
        } else {
          dispatch(setInvalidAccessKey(true));
        }
      })
      .catch(() => {
        dispatch(setInvalidAccessKey(true));
      })
      .finally(() => {
        setGateLoading(false);
      });
  };

  if (gateActive) {
    return (
      <CookiesProvider>
        <ThemeProvider theme={theme}>
          <div className="App">
            <AccessGate
              invalidAccessKey={invalidAccessKey}
              onSubmit={handleAccessCodeSubmit}
              loading={gateLoading}
            />
          </div>
        </ThemeProvider>
      </CookiesProvider>
    );
  }

  return (
    <CookiesProvider>
      <ThemeProvider theme={theme}>
        <div className="App">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/input" element={<InputPage />} />
            <Route path="/loading" element={<Loading />} />
            <Route path="/pdf" element={<PDF />} />
            <Route path="/analysis" element={<Analysis />}>
              <Route path="roi" element={<ROI />} />
              <Route path="incentives" element={<Incentives />} />
              <Route path="costs" element={<Costs />} />
              <Route path="faqs" element={<FAQs />} />
            </Route>
          </Routes>
        </div>
      </ThemeProvider>
    </CookiesProvider>
  );
}

export default App;
